import {
  Box,
  TextField,
  Typography,
  IconButton,
  Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TableColumn {
  id: string;
  header: string;
  required?: boolean;
}

export interface TableData {
  tableRows: Array<Record<string, string>>;
}

interface TableParameterFieldProps {
  label: string;
  columns: TableColumn[];
  value: TableData | null;
  onChange: (v: TableData) => void;
  required?: boolean;
  /** When true, all inputs are disabled and add/remove controls are hidden. */
  readonly?: boolean;
  placeholder?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function makeEmptyRow(columns: TableColumn[]): Record<string, string> {
  const row: Record<string, string> = {};
  for (const c of columns) {
    row[c.id] = '';
    if (c.header !== c.id) row[c.header] = '';
  }
  return row;
}

function toCellString(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (obj.label != null && obj.label !== '') return String(obj.label);
    if (obj.value != null && obj.value !== '') return toCellString(obj.value);
    if (obj.amount != null && obj.amount !== '') return toCellString(obj.amount);
    if (obj.input != null && obj.input !== '') return toCellString(obj.input);
  }
  return '';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Reusable dynamic-table parameter field with add/remove row controls.
 * Mirrors Angular's `app-table-parameters` component.
 *
 * Column headers are driven by the `columns` prop (from `tableColumns` in the catalog definition).
 * Each cell stores its value keyed by column header, matching the Angular FormArray structure.
 */
export function TableParameterField({
  label,
  columns,
  value,
  onChange,
  required = false,
  readonly = false,
  placeholder,
}: TableParameterFieldProps) {
  // Ensure at least one default empty row is always present
  let tableRows = value?.tableRows ?? [];
  if (tableRows.length === 0) {
    tableRows = [makeEmptyRow(columns)];
  }
  const current: TableData = { tableRows };

  const getCell = (row: Record<string, string>, col: TableColumn): string => {
    const raw = (row as Record<string, unknown>)[col.id] ?? (row as Record<string, unknown>)[col.header];
    return toCellString(raw);
  };

  const updateCell = (rowIdx: number, header: string, val: string) => {
    const col = columns.find((c) => c.header === header || c.id === header);
    const canonicalKey = col?.id ?? header;
    const rows = current.tableRows.map((row, i) =>
      i === rowIdx
        ? {
            ...row,
            [canonicalKey]: val,
            ...(col && col.header !== canonicalKey ? { [col.header]: val } : {}),
          }
        : row,
    );
    onChange({ tableRows: rows });
  };

  const addRow = () => {
    onChange({ tableRows: [...current.tableRows, makeEmptyRow(columns)] });
  };

  const removeRow = (rowIdx: number) => {
    // Only allow deletion if there's more than one row
    if (current.tableRows.length > 1) {
      onChange({ tableRows: current.tableRows.filter((_, i) => i !== rowIdx) });
    }
  };

  const cellSx = {
    p: 0.25,
    borderBottom: '1px solid',
    borderColor: 'divider',
  } as const;

  return (
    <Box>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {label}
          {required && <span style={{ color: 'red' }}> *</span>}
        </Typography>
      )}

      <Box sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Box
          component="table"
          sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}
        >
          {/* Header */}
          <Box component="thead">
            <Box component="tr">
              {columns.map((col) => (
                <Box
                  key={col.id}
                  component="th"
                  sx={{
                    ...cellSx,
                    textAlign: 'left',
                    fontWeight: 600,
                    color: 'text.secondary',
                    minWidth: 100,
                    whiteSpace: 'nowrap',
                    userSelect: 'none',
                    px: 1,
                  }}
                >
                  {col.header}
                  {col.required && <span style={{ color: 'red' }}> *</span>}
                </Box>
              ))}
              {!readonly && (
                <Box
                  component="th"
                  sx={{
                    ...cellSx,
                    width: 36,
                    textAlign: 'center',
                    fontWeight: 600,
                    color: 'text.secondary',
                  }}
                >
                  Actions
                </Box>
              )}
            </Box>
          </Box>

          {/* Body */}
          <Box component="tbody">
            {current.tableRows.map((row, rowIdx) => (
              <Box component="tr" key={rowIdx}>
                {columns.map((col) => (
                  <Box key={col.id} component="td" sx={cellSx}>
                    <TextField
                      size="small"
                      value={getCell(row, col)}
                      onChange={(e) => updateCell(rowIdx, col.header, e.target.value)}
                      disabled={readonly}
                      slotProps={{ htmlInput: { style: { padding: '4px 8px', fontSize: 12 } } }}
                      fullWidth
                    />
                  </Box>
                ))}
                {!readonly && (
                  <Box component="td" sx={{ ...cellSx, textAlign: 'center' }}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeRow(rowIdx)}
                      disabled={current.tableRows.length <= 1}
                      title={current.tableRows.length <= 1 ? 'Cannot delete the only row' : 'Delete row'}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {!readonly && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={addRow}
          sx={{ mt: 1 }}
          variant="outlined"
        >
          Add Row
        </Button>
      )}
    </Box>
  );
}
